/**
 * Select — on Radix, per §4.2 (shadcn/ui over Radix primitives).
 *
 * Radix rather than a native `<select>`, which is what this used to be. The
 * native element gives you the platform picker on a phone for free; what it does
 * not give you is a styleable option list, and a design system that can style
 * every control except one has a hole in it.
 *
 * Two consequences worth knowing before using it:
 *
 * 1. **It is not a form-native control.** There is no `<select name>` in the
 *    output, so `{...register('level')}` does nothing. Bind with `SelectField`,
 *    which wraps `Controller` — see below.
 * 2. **`dir` has to be passed.** Radix decides which side to align the list and
 *    the check mark from it, and it does not read `<html dir>` itself.
 */
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'

import { useLocale } from '@/shared/i18n/useLocale'

import { cn } from './cn'
import { useFieldProps } from './fieldContext'

export type SelectOption = { value: string; label: string; disabled?: boolean | undefined }

export type SelectProps = {
  options: readonly SelectOption[]
  value?: string | undefined
  onValueChange?: ((value: string) => void) | undefined
  /**
   * Shown when nothing is chosen. Radix cannot hold `''` as a value — an empty
   * string is how it spells "no selection" internally — so a caller wanting a
   * clearable field gets `''` back through `onValueChange` when the placeholder
   * option is picked, and `EMPTY` is the sentinel that makes that possible.
   */
  placeholder?: string | undefined
  /** Offer the placeholder as a real, choosable option. */
  clearable?: boolean | undefined
  disabled?: boolean | undefined
  name?: string | undefined
  className?: string | undefined
}

/** Radix reserves `''`. This stands in for "nothing chosen" inside the list. */
const EMPTY = '__none__'

export function Select({
  options,
  value,
  onValueChange,
  placeholder,
  clearable = false,
  disabled,
  name,
  className,
}: SelectProps) {
  const field = useFieldProps()
  const { direction } = useLocale()

  return (
    <SelectPrimitive.Root
      dir={direction}
      // Spread rather than passed: Radix declares these optional *without*
      // `| undefined`, and `exactOptionalPropertyTypes` rejects handing one an
      // explicit undefined.
      {...(value === undefined ? {} : { value: value === '' ? EMPTY : value })}
      onValueChange={(next) => onValueChange?.(next === EMPTY ? '' : next)}
      {...(disabled ? { disabled } : {})}
      {...(name ? { name } : {})}
    >
      <SelectPrimitive.Trigger
        {...field}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-[5px] border border-line-strong',
          'bg-surface px-3 text-sm text-fg',
          'data-placeholder:text-fg-subtle',
          'aria-invalid:border-danger aria-invalid:ring-1 aria-invalid:ring-danger/30',
          'disabled:cursor-not-allowed disabled:bg-sunken disabled:opacity-70',
          className,
        )}
      >
        {/* `text-start` so a long label truncates from the correct edge. */}
        <SelectPrimitive.Value placeholder={placeholder} className="truncate text-start" />
        <SelectPrimitive.Icon asChild>
          <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-fg-subtle" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          // `popper` rather than the default `item-aligned`: item-aligned puts
          // the list over the trigger, which on a long list covers the label the
          // user is reading.
          position="popper"
          sideOffset={4}
          className={cn(
            'z-50 max-h-72 min-w-(--radix-select-trigger-width) overflow-hidden rounded-lg',
            'border border-line bg-raised shadow-lg ring-1 ring-edge',
          )}
        >
          <SelectPrimitive.ScrollUpButton className="flex h-6 items-center justify-center text-fg-subtle">
            <ChevronUp aria-hidden="true" className="size-4" />
          </SelectPrimitive.ScrollUpButton>

          <SelectPrimitive.Viewport className="p-1">
            {clearable && placeholder ? <Item value={EMPTY} label={placeholder} muted /> : null}
            {options.map((option) => (
              <Item
                key={option.value}
                value={option.value}
                label={option.label}
                disabled={option.disabled}
              />
            ))}
          </SelectPrimitive.Viewport>

          <SelectPrimitive.ScrollDownButton className="flex h-6 items-center justify-center text-fg-subtle">
            <ChevronDown aria-hidden="true" className="size-4" />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}

function Item({
  value,
  label,
  disabled,
  muted = false,
}: {
  value: string
  label: string
  disabled?: boolean | undefined
  muted?: boolean
}) {
  return (
    <SelectPrimitive.Item
      value={value}
      {...(disabled ? { disabled } : {})}
      className={cn(
        // `relative` anchors the check mark, which is absolutely positioned.
        // `pe-2 ps-8` — the mark sits at the inline start, so the padding that
        // reserves room for it has to mirror too.
        'relative flex cursor-pointer items-center gap-2 rounded-md py-2 pe-2 ps-8 text-sm outline-none',
        'data-highlighted:bg-sunken',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        muted && 'text-fg-muted',
      )}
    >
      <SelectPrimitive.ItemIndicator className="absolute start-2 inline-flex">
        <Check aria-hidden="true" className="size-4" />
      </SelectPrimitive.ItemIndicator>
      <SelectPrimitive.ItemText>{label}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}
